import type { StepperProps, StepStatus } from './types';
import styles from './Stepper.module.scss';

// ============================================
// HELPERS
// ============================================

function getStepStatus(stepIndex: number, activeStep: number): StepStatus {
  if (stepIndex < activeStep) return 'completed';
  if (stepIndex === activeStep) return 'active';
  return 'upcoming';
}

// ============================================
// COMPONENT
// ============================================

function Stepper({ steps, activeStep, onStepClick, className }: StepperProps) {
  return (
    <nav
      className={`${styles.stepper} ${className ?? ''}`.trim()}
      aria-label="Progress"
    >
      <ol className={styles.stepList}>
        {steps.map((step, index) => {
          const status = getStepStatus(index, activeStep);
          const isClickable = status === 'completed' && Boolean(onStepClick);

          return (
            <li key={step.id} className={styles.stepItem}>
              {/* Connector line before circle (not on first step) */}
              {index > 0 && (
                <div
                  className={`${styles.connector} ${index <= activeStep ? styles.connectorCompleted : ''}`}
                  aria-hidden="true"
                />
              )}

              {/* Circle indicator */}
              <button
                type="button"
                className={`${styles.circle} ${styles[status]}`}
                onClick={isClickable ? () => onStepClick!(index) : undefined}
                disabled={!isClickable}
                aria-current={status === 'active' ? 'step' : undefined}
                aria-label={`${step.label}${status === 'completed' ? ' (completed)' : status === 'active' ? ' (current)' : ''}`}
                tabIndex={isClickable ? 0 : -1}
              >
                {status === 'completed' && (
                  <svg
                    className={styles.checkIcon}
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 6L5 8.5L9.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {/* Step label below circle */}
              <span
                className={`${styles.label} ${status === 'active' ? styles.labelActive : ''} ${status === 'completed' ? styles.labelCompleted : ''}`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Stepper;
